import farmSo from "stores/farm"
import growUnitSo from "stores/growUnit"
import { getByPageId, isChild } from "stores/mainMenu/utils"
import locationSo from "stores/route/location"
import querySo from "stores/route/query"
import { PAGE_ID } from "stores/route/utils/types"
import userSo from "stores/user"
import { COMPONENT_TYPE, Component } from "types/Service"



const isPLC = import.meta.env.VITE_TARGET == "plc"

/**
 * restituisce un URL a fronte di un PAGE-ID
 * @param pageId
 * @param id identificativo aggiunto utile nelle pagine di dettaglio
 * @param nestedId identificativo aggiunto utile nelle pagine con dettaglio annidato
 */
export function getUrlByPageId(pageId: PAGE_ID, id?: any, nestedId?: string): string {
    if (!pageId) return null

    // preservo i parametri di ricerca
    const q = `?${querySo.state.queryUrl}`

    switch (pageId) {

        //#region FARMS

        case PAGE_ID.FARM_GROWUNITS:
            return buildFarmUrl(`/farms/{id}/growunits`, id)
        case PAGE_ID.FARM_PLANNER:
            return buildFarmUrl(`/farms/{id}/planner${q}`, id)
        case PAGE_ID.FARM_CYCLES:
            return buildFarmUrl(`/farms/{id}/cycles${q}`, id)
        case PAGE_ID.FARM_BI:
            return buildFarmUrl(`/farms/{id}/bi${q}`, id)
        case PAGE_ID.FARM_MAP:
            return buildFarmUrl(`/farms/{id}/map${q}`, id)
        case PAGE_ID.FARM_ATTACH:
            return buildFarmUrl(`/farms/{id}/attach${q}`, id)

        case PAGE_ID.FARM_TASKS:
            return buildFarmUrl(`/farms/{id}/tasks${q}`, id)
        case PAGE_ID.FARM_AI_PLANNING:
            return buildFarmUrl(`/farms/{id}/productions${q}`, id)
        case PAGE_ID.FARM_ALERTS:
            return buildFarmUrl(`/farms/{id}/alerts${q}`, id)

        case PAGE_ID.FARM_PLC_ALERTS:
            return buildFarmUrl(`/farms/{id}/plc_alerts`, id)

        case PAGE_ID.COMPONENT_POWER_MONITOR:
            return buildFarmUrl(`/farms/{id}/powers`, id)

        case PAGE_ID.FARM_CHARTS:
            return buildFarmUrl(`/farms/{id}/charts`, id)
        case PAGE_ID.FARM_CONSOLES:
            return buildFarmUrl(`/farms/{id}/consoles`, id)
        case PAGE_ID.FARM_TECHNICAL_DASHBOARDS:
            return buildFarmUrl(`/farms/{id}/technical-dashboards`, id)
        case PAGE_ID.FARM_BUSINESS_DASHBOARDS:
            return buildFarmUrl(`/farms/{id}/business-dashboards`, id)
        case PAGE_ID.FARM_EXTERNAL_LINK:
            return buildFarmUrl(`/farms/{id}/external-link/${nestedId}`, id)

        //#endregion



        //#region GROWUNITS

        case PAGE_ID.GROWUNIT_TOWERS:
            return buildGrowUnitUrl(`/growunits/{id}/layers`, id)
        case PAGE_ID.GROWUNIT_RECIPE:
            return buildGrowUnitUrl(`/growunits/{id}/recipe`, id)

        case PAGE_ID.GROWUNIT_HOME:
            return buildComponentUrl(`/farms/{id}/{code}/home`, id, nestedId)
        case PAGE_ID.GROWUNIT_MAIN:
            return buildComponentUrl(`/farms/{id}/{code}/main`, id, nestedId)
        case PAGE_ID.GROWUNIT_LAYERS:
            return buildComponentUrl(`/farms/{id}/{code}/layers`, id, nestedId)
        case PAGE_ID.GROWUNIT_FERTI:
            return buildComponentUrl(`/farms/{id}/{code}/ferti`, id, nestedId)
        case PAGE_ID.GROWUNIT_ADJ:
            return buildComponentUrl(`/farms/{id}/{code}/adj`, id, nestedId)
        case PAGE_ID.GROWUNIT_LIGHTING:
            return buildComponentUrl(`/farms/{id}/{code}/lighting`, id, nestedId)
        case PAGE_ID.GROWUNIT_LIGHTING_PS:
            return buildComponentUrl(`/farms/{id}/{code}/lightingps`, id, nestedId)
        case PAGE_ID.GROWUNIT_CLIMATE:
            return buildComponentUrl(`/farms/{id}/{code}/climate`, id, nestedId)
        case PAGE_ID.GROWUNIT_IRRIGATION:
            return buildComponentUrl(`/farms/{id}/{code}/irrigation`, id, nestedId)
        case PAGE_ID.GROWUNIT_PLC_ALERTS:
            return buildComponentUrl(`/farms/{id}/{code}/gu/alerts`, id, nestedId)
        case PAGE_ID.GROWUNIT_PLC_PROPERTIES:
            return buildComponentUrl(`/farms/{id}/{code}/gu/properties`, id, nestedId)
        case PAGE_ID.COMPONENT_SCHEDULER:
            return buildComponentUrl(`/farms/{id}/{code}/scheduler`, id, nestedId)

        //#region COMPONENTS

        case PAGE_ID.COMPONENT_OSMOTIC_WATER:
            return buildComponentUrl(`/farms/{id}/{code}/ow`, id, nestedId)
        case PAGE_ID.COMPONENT_OSMOTIC_WATER_ALERTS:
            return buildComponentUrl(`/farms/{id}/{code}/ow/alerts`, id, nestedId)
        case PAGE_ID.COMPONENT_OSMOTIC_WATER_PROPERTIES:
            return buildComponentUrl(`/farms/{id}/{code}/ow/properties`, id, nestedId)

        case PAGE_ID.COMPONENT_HVAC:
            return buildComponentUrl(`/farms/{id}/{code}/hvac`, id, nestedId)
        case PAGE_ID.COMPONENT_HVAC_ALERTS:
            return buildComponentUrl(`/farms/{id}/{code}/hvac/alerts`, id, nestedId)
        case PAGE_ID.COMPONENT_HVAC_PROPERTIES:
            return buildComponentUrl(`/farms/{id}/{code}/hvac/properties`, id, nestedId)

        case PAGE_ID.COMPONENT_GERMINATION:
            return buildComponentUrl(`/farms/{id}/{code}/germ`, id, nestedId)
        case PAGE_ID.COMPONENT_GERMINATION_ALERTS:
            return buildComponentUrl(`/farms/{id}/{code}/germ/alerts`, id, nestedId)
        case PAGE_ID.COMPONENT_GERMINATION_PROPERTIES:
            return buildComponentUrl(`/farms/{id}/{code}/germ/properties`, id, nestedId)

        case PAGE_ID.COMPONENT_CO2:
            return buildComponentUrl(`/farms/{id}/{code}/co2`, id, nestedId)
        case PAGE_ID.COMPONENT_CO2_PANEL:
            return buildComponentUrl(`/farms/{id}/{code}/co2/panel`, id, nestedId)
        case PAGE_ID.COMPONENT_CO2_ALERTS:
            return buildComponentUrl(`/farms/{id}/{code}/co2/alerts`, id, nestedId)
        case PAGE_ID.COMPONENT_CO2_PROPERTIES:
            return buildComponentUrl(`/farms/{id}/{code}/co2/properties`, id, nestedId)

        case PAGE_ID.COMPONENT_WATER_TREATMENT:
            return buildComponentUrl(`/farms/{id}/{code}/wt`, id, nestedId)
        case PAGE_ID.COMPONENT_WATER_TREATMENT_ALERTS:
            return buildComponentUrl(`/farms/{id}/{code}/wt/alerts`, id, nestedId)
        case PAGE_ID.COMPONENT_WATER_TREATMENT_PROPERTIES:
            return buildComponentUrl(`/farms/{id}/{code}/wt/properties`, id, nestedId)


        case PAGE_ID.COMPONENT_THERMAL_POWER_PLANT:
            return buildComponentUrl(`/farms/{id}/{code}/thermal`, id, nestedId)


        //#region PLC CONSOLE

        case PAGE_ID.FARM_MODULES:
            return buildFarmUrl(`/farms/{id}/modules`, id)
        case PAGE_ID.FARM_METADATA:
            return buildFarmUrl(`/farms/{id}/metadata`, id)

        //#endregion
        //#endregion
        //#endregion



        //#region LIBRARY

        case PAGE_ID.RECIPES:
            return `/recipes${id ? `/${id}` : ""}`
        case PAGE_ID.RECIPES_DETAIL:
            return `/recipes/${id}` // id obbligatorio
        case PAGE_ID.CROPS:
            return `/crops${id ? `/${id}` : ""}`
        case PAGE_ID.CROPS_DETAIL:
            return `/crops/${id}` // id obbligatorio
        case PAGE_ID.CROPS_RECIPES:
            return `/crops_recipes${id ? `/${id}` : ""}`
        case PAGE_ID.CROPS_RECIPES_DETAIL:
            return `/crops_recipes/${id}`
        case PAGE_ID.PRODUCTS:
            return `/products${id ? `/${id}` : ""}`

        //#endregion



        //#region SETTINGS

        case PAGE_ID.ALARMS:
            return `/alarms${id ? `/${id}` : ""}`

        //#endregion



        //#region LABS

        case PAGE_ID.LABS_PLANNER:
            return `/labs_planner`
        case PAGE_ID.LABS_SOLUTIONS:
            return `/labs_solutions`
        case PAGE_ID.LABS_GPT:
            return `/labs_gpt`

        //#endregion


        case PAGE_ID.PREFERENCES:
            return `/preferences`
        default:
            return `/${pageId}`
    }
}
function buildGrowUnitUrl(url: string, id: string): string {
    if (!id) id = growUnitSo.state.select?.id?.toString()
    return id ? url.replace("{id}", id) : null
}
function buildFarmUrl(url: string, id: number) {
    const idStr = isPLC
        ? "current"
        : (id ? id.toString() : farmSo.state.select?.id?.toString())
    return url.replace("{id}", idStr)
}
/** costruisce l'url del COMPONENT eventualmente valorizzando l'id  */
function buildComponentUrl(url: string, farmId: number, code?: string): string {
    url = buildFarmUrl(url, farmId)
    if (!code) code = farmSo.state.componentSelect?.code
    return code ? url.replace("{code}", code) : null
}


/** Restituisce un titolo in base alla pagina (tipicamente usato nell'header) */
export function getHeaderTitleByPageId(pageId: PAGE_ID): string {
    const component = farmSo.state.componentSelect
    if (isPLC) {
        return component?.name ?? "--"
    } else {
        if (pageId == PAGE_ID.FARMS) {
            return farmSo.state.select?.name
        } else if (pageId == PAGE_ID.FARM_GROWUNITS) {
            return component?.name ?? "--"
        }
    }
}



/** 
 * url per la pagina di una specifica Farm 
 * se la pagina precedente era "child" della sezione FARM allora torna alla precedente altrimenti va in DETAIL
*/
export function getUrlFromFarmId(farmId: number): string {
    if (!farmId) return null

    // pagina di default
    let pageId: PAGE_ID = PAGE_ID.FARM_MAP

    // la pagina attuale/precedente è nella sezione FARMS e allora prendi quella
    if (isChild(locationSo.state.currentPage, PAGE_ID.FARMS, true)) {
        pageId = locationSo.state.currentPage
    } else if (isChild(locationSo.state.previousPage, PAGE_ID.FARMS, true)) {
        pageId = locationSo.state.previousPage
    }

    return getUrlByPageId(pageId, farmId)
}

/** 
 * url per la pagina di una specifica GrowUnit 
 * se la pagina precedente era "child" della sezione GROWUNIT allora torna alla sezione precedente altrimenti va in DETAIL
 * [II] da eliminare insieme alla lista della vecchia lista FARM
*/
export function getUrlFromGrowUnitId(growUnitId: number): string {
    if (!growUnitId) return null

    // pagina di default
    let pageId: PAGE_ID = PAGE_ID.GROWUNIT_HOME

    // la pagina attuale/precedente è nella sezione GROWUNITS e allora prendi quella
    if (isChild(locationSo.state.currentPage, PAGE_ID.FARM_GROWUNITS, true)) {
        pageId = locationSo.state.currentPage
    } else if (isChild(locationSo.state.previousPage, PAGE_ID.FARM_GROWUNITS, true)) {
        pageId = locationSo.state.previousPage
    }

    // se è TOWERS o RECIPE non c'e' bisogno del COMPONENT
    if (pageId == PAGE_ID.GROWUNIT_TOWERS || pageId == PAGE_ID.GROWUNIT_RECIPE) {
        return getUrlByPageId(pageId, growUnitId)
    }

    // ricavo il COMPONENT e vado...
    const farm = farmSo.state.select
    const component = farmSo.getComponentByGrowUnitCode(farmSo.getGrowUnit(growUnitId)?.growUnitCode)
    return getUrlByPageId(pageId, farm?.id, component?.code)
}

/** 
 * url per la pagina di uno specifico component 
 * se la pagina precedente era "child" della sezione GROWUNIT 
 * allora torna alla sezione precedente altrimenti va in HOME
*/
export function getUrlFromComponent(component: Component): string {

    const { parents, sections, index } = getByPageId(locationSo.state.currentPage) ?? {}
    const group = sections[index]?.group
    const code = component.code
    const farmId = farmSo.state.select?.id

    // se la pagina attuale è nella sezione GROWUNITS ed è lo stesso "group" 
    // allora vai nella nuova GROWUNIT mantenendo la stessa pagina (es. RECIPE, LAYERS, ecc...)
    if (isChild(locationSo.state.currentPage, PAGE_ID.FARM_GROWUNITS, true) && component.type == group) {
        return getUrlByPageId(locationSo.state.currentPage, farmId, code)
    }

    // se la pagina precedente è nella sezione GROWUNITS ed è lo stesso "group"
    // allora vai nella nuova GROWUNIT mantenendo la stessa pagina (es. RECIPE, LAYERS, ecc...)
    if (isChild(locationSo.state.previousPage, PAGE_ID.FARM_GROWUNITS, true) && component.type == group) {
        return getUrlByPageId(locationSo.state.previousPage, farmId, code)
    }

    // sceglie la pagina di destinazione in base al tipo di COMPONENT
    if (!isPLC && component.type == COMPONENT_TYPE.GROWUNIT) {
        return getUrlByPageId(
            userSo.plcAccess() ? PAGE_ID.GROWUNIT_HOME : PAGE_ID.GROWUNIT_LAYERS,
            farmId,
            code
        )
    }
    const pageId = getPageIdFromComponentType(component.type)
    return getUrlByPageId(pageId, farmId, code)
}

/** 
 * Restituisce il PAGE-ID in base al COMPONENT-TYPE specificato 
 */
export function getPageIdFromComponentType(type: COMPONENT_TYPE): PAGE_ID {
    return {
        [COMPONENT_TYPE.GROWUNIT]: PAGE_ID.GROWUNIT_HOME,
        [COMPONENT_TYPE.GERMINATOR]: PAGE_ID.COMPONENT_GERMINATION,
        [COMPONENT_TYPE.HVAC]: PAGE_ID.COMPONENT_HVAC,
        [COMPONENT_TYPE.POWERS]: PAGE_ID.COMPONENT_POWER_MONITOR,
        [COMPONENT_TYPE.OSMOTIC_WATER]: PAGE_ID.COMPONENT_OSMOTIC_WATER,
        [COMPONENT_TYPE.CO2]: PAGE_ID.COMPONENT_CO2,
        [COMPONENT_TYPE.CO2_PANEL]: PAGE_ID.COMPONENT_CO2_PANEL,
        [COMPONENT_TYPE.THERMAL_POWER_PLANT]: PAGE_ID.COMPONENT_THERMAL_POWER_PLANT,
        [COMPONENT_TYPE.WATER_TREATMENT]: PAGE_ID.COMPONENT_WATER_TREATMENT,
        [COMPONENT_TYPE.CLIMATE]: PAGE_ID.GROWUNIT_CLIMATE,
    }[type] ?? PAGE_ID.GROWUNIT_HOME
}